import React, { ChangeEvent, FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { TextArea } from 'transfix-ui/components/TextArea'
import {
  AddEntityForm,
  AddEntityFormFooter,
} from 'apps/fleet-planner/components/forms/AddEntityForm'
import { IAddNewEntityFormProps } from 'apps/fleet-planner/components/forms/TrailerForm'
import { CustomerSchema, CustomerSchemaType } from 'apps/fleet-planner/schema/CustomerSchema'
import { TextInput } from 'components/atoms'
import { Button, PhoneInput } from 'transfix-ui'
import formatPhone, { unformatPhone } from 'utils/formatPhone'

const emptyCustomerState: CustomerSchemaType = {
  contact: {
    name: '',
    phone: '',
    phoneExt: '',
    email: '',
  },
  name: '',
  notes: '',
}

/**
 * Removes null values from defaults
 */
const getDefaultCustomerValues = (data: Partial<CustomerSchemaType>): CustomerSchemaType => {
  return {
    contact: {
      name: data?.contact?.name ?? '',
      phone: data?.contact?.phone ?? '',
      phoneExt: data?.contact?.phoneExt ?? '',
      email: data?.contact?.email ?? '',
    },
    name: data?.name ?? '',
    notes: data?.notes ?? '',
  }
}

export interface ICustomerFormProps extends IAddNewEntityFormProps<CustomerSchemaType> {}

export const CustomerForm: FC<ICustomerFormProps> = ({
  onSuccess: onSuccessProp,
  onCancel: onCancelProp = () => {},
  defaultValues: defaultValuesProp = {},
  isLoading: isLoadingProp,
}) => {
  const defaultValues = getDefaultCustomerValues(defaultValuesProp)
  const { control, errors, handleSubmit } = useForm({
    mode: 'all',
    resolver: yupResolver(CustomerSchema),
    defaultValues: CustomerSchema.cast({
      ...emptyCustomerState,
      ...defaultValues,
    }),
  })

  const submitHandler = (data: CustomerSchemaType) => {
    onSuccessProp(data)
  }

  const isEditingExisitingCustomer = !!defaultValuesProp.id

  return (
    <AddEntityForm dataTest='add-customer-form' onSubmit={handleSubmit(submitHandler)}>
      {/* Customer name */}
      <div className='mb4'>
        <Controller
          name='name'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextInput
                large
                autoFocus
                required
                label='Customer Name'
                value={value}
                placeholder='The name of your shipper or broker'
                onChange={changeHandler}
                onBlur={onBlur}
                validationMessage={errors?.name?.message}
                invalid={!!errors?.name?.message}
                dataTest='customer-name-input'
              />
            )
          }}
        />
      </div>
      {/* Customer contact name */}
      <div className='mb4'>
        <Controller
          name='contact.name'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextInput
                label='Customer Contact Name'
                value={value}
                placeholder='Jane Doe'
                onChange={changeHandler}
                large
                onBlur={onBlur}
                validationMessage={errors?.contact?.name?.message}
                invalid={!!errors?.contact?.name?.message}
                dataTest='customer-contact-name-input'
              />
            )
          }}
        />
      </div>

      <div className='flex mb4'>
        <div className='flex-auto pr2'>
          {/* Customer contact phone */}
          <Controller
            name='contact.phone'
            control={control}
            render={({ onBlur, onChange, value }) => {
              const formattedValue = formatPhone(value, { removeCountryCode: '+1' })
              const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
                onChange(unformatPhone(e.target.value))
              }

              return (
                <PhoneInput
                  autoComplete='off'
                  label='Customer Contact Phone'
                  onChange={changeHandler}
                  onBlur={onBlur}
                  placeholder='(000) 000-0000'
                  value={formattedValue}
                  large
                  validationMessage={errors?.contact?.phone?.message}
                  invalid={!!errors?.contact?.phone?.message}
                  dataTest='customer-contact-phone-input'
                  className='fs-mask'
                />
              )
            }}
          />
        </div>
        <div className='w-40 pl2'>
          {/* Customer contact phone extension */}
          <Controller
            name='contact.phoneExt'
            control={control}
            render={({ onBlur, onChange, value }) => {
              const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
                onChange(e.target.value)
              }

              return (
                <TextInput
                  label='Extension'
                  value={value}
                  onChange={changeHandler}
                  large
                  onBlur={onBlur}
                  validationMessage={errors?.contact?.phoneExt?.message}
                  invalid={!!errors?.contact?.phoneExt?.message}
                  dataTest='customer-contact-phoneExt-input'
                />
              )
            }}
          />
        </div>
      </div>

      {/* Customer contact email */}
      <div className='mb4'>
        <Controller
          name='contact.email'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextInput
                label='Customer Contact Email'
                value={value}
                onChange={changeHandler}
                placeholder='jane.doe@domain.com'
                large
                onBlur={onBlur}
                validationMessage={errors?.contact?.email?.message}
                invalid={!!errors?.contact?.email?.message}
                dataTest='customer-contact-email-input'
                className='fs-mask'
              />
            )
          }}
        />
      </div>

      {/* Customer notes */}
      <div className='mb5'>
        <Controller
          name='notes'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLTextAreaElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextArea
                placeholder='Write internal notes about this customer...'
                label='Customer Notes'
                size='large'
                dataTest='customer-notes-input'
                value={value}
                onBlur={onBlur}
                onChange={changeHandler}
              />
            )
          }}
        />
      </div>
      <AddEntityFormFooter>
        <Button
          color='tertiary'
          size='large'
          type='button'
          onClick={onCancelProp}
          data-test='cancel-new-customer'>
          Cancel
        </Button>
        <Button
          color='primary'
          disabled={isLoadingProp}
          size='large'
          type='submit'
          data-test='save-new-customer'>
          {isEditingExisitingCustomer ? 'Save changes' : 'Add Customer'}
        </Button>
      </AddEntityFormFooter>
    </AddEntityForm>
  )
}
